"use client";

import { track } from "@opensea/analytics";
import { useRouter } from "@opensea/navigation";
import { classNames, Flex, FlexColumn } from "@opensea/ui-kit";
import { useEventListener, useIsHydrated, useOnChange, useScrollLock } from "@opensea/ui-kit/hooks";
import { useIsReady } from "@opensea/wallet";
import { useEffect, useState } from "react";
import { useCurrencySwitch } from "@/hooks/use-currency-switch";
import { useDisconnect } from "@/hooks/use-disconnect";
import { usePersonaSwitch } from "@/hooks/use-persona-switch";
import { useAddress } from "@/hooks/wallet";
import { withSuspense } from "../suspense/WithSuspense";
import { OnboardingBackground } from "./OnboardingBackground";
import { useOnboardingGuard } from "./OnboardingGuard";
import { OnboardingHeader } from "./OnboardingHeader";
import { CurrencyStep } from "./screens/CurrencyStep";
import { FilmStep } from "./screens/FilmStep";
import { IntroStep } from "./screens/IntroStep";
import { OutroStep } from "./screens/OutroStep";
import { PersonaStep } from "./screens/PersonaStep";
import { RewardsStep } from "./screens/RewardsStep";
const DEFAULT_PERSONA = "collector";
const DEFAULT_CURRENCY = "crypto";
export const onboardingSteps = ["intro", "film", "persona", "currency", "rewards", "outro"];
export const Onboarding = withSuspense(function Onboarding() {
  const hydrated = useIsHydrated();
  const {
    redirect,
    setCompleted: setOnboardingCompleted
  } = useOnboardingGuard();
  const [introCompleted, setIntroCompleted] = useState(false);
  const [filmCompleted, setFilmCompleted] = useState(false);
  const [persona, setPersona] = useState(null);
  const [currencyPreference, setCurrencyPreference] = useState(null);
  const [rewardsCompleted, setRewardsCompleted] = useState(false);
  const [outroCompleted, setOutroCompleted] = useState(false);
  const {
    setPersona: setPersistedPersona
  } = usePersonaSwitch();
  const {
    setCurrency: setPersistedCurrency
  } = useCurrencySwitch();

  /**
   * Handle Step
   */
  function getCurrentStep() {
    for (const step of onboardingSteps) {
      switch (step) {
        case "intro":
          if (!introCompleted) {
            return "intro";
          }
          break;
        case "film":
          if (!filmCompleted) {
            return "film";
          }
          break;
        case "persona":
          if (!persona) {
            return "persona";
          }
          break;
        case "currency":
          if (!currencyPreference) {
            return "currency";
          }
          break;
        case "rewards":
          if (!rewardsCompleted) {
            return "rewards";
          }
          break;
        case "outro":
          if (!outroCompleted) {
            return "outro";
          }
          break;
      }
    }
  }
  const currentStep = getCurrentStep();
  function setCurrentStep(step) {
    switch (step) {
      case "intro":
        setIntroCompleted(false);
        break;
      case "film":
        setIntroCompleted(true);
        setFilmCompleted(false);
        break;
      case "persona":
        setIntroCompleted(true);
        setFilmCompleted(true);
        setPersona(null);
        break;
      case "currency":
        setIntroCompleted(true);
        setFilmCompleted(true);
        setPersona(persona ?? DEFAULT_PERSONA);
        setCurrencyPreference(null);
        break;
      case "rewards":
        setIntroCompleted(true);
        setFilmCompleted(true);
        setPersona(persona ?? DEFAULT_PERSONA);
        setCurrencyPreference(currencyPreference ?? DEFAULT_CURRENCY);
        setRewardsCompleted(false);
        break;
      case "outro":
        setIntroCompleted(true);
        setFilmCompleted(true);
        setPersona(persona ?? DEFAULT_PERSONA);
        setCurrencyPreference(currencyPreference ?? DEFAULT_CURRENCY);
        setRewardsCompleted(true);
        setOutroCompleted(false);
        break;
    }
  }
  const router = useRouter();
  function setCompleted() {
    if (!persona) {
      setPersona(DEFAULT_PERSONA);
    }
    if (!currencyPreference) {
      setCurrencyPreference(DEFAULT_CURRENCY);
    }

    // Make sure everything is completed
    setIntroCompleted(true);
    setFilmCompleted(true);
    setPersistedPersona(persona || DEFAULT_PERSONA);
    setPersistedCurrency(currencyPreference || DEFAULT_CURRENCY);
    setRewardsCompleted(true);
    setOutroCompleted(true);
    setOnboardingCompleted(true);
    if (redirect) {
      setTimeout(() => {
        router.replace(redirect);
      }, 500 + 300); // duration + delay
    }
    void track("Onboarding Complete", {
      persona,
      currencyPreference
    });
  }

  /**
   * esc support
   */
  useEventListener("keydown", event => {
    switch (event.key) {
      case "Escape":
        {
          const stepIndex = currentStep ? onboardingSteps.indexOf(currentStep) : -1;
          if (stepIndex > 0) {
            if (stepIndex < onboardingSteps.length - 1) {
              const lastStep = onboardingSteps[stepIndex - 1];
              setCurrentStep(lastStep);
            }
          } else {
            setCompleted();
          }
        }
    }
  });

  /**
   * Lock scroll
   */
  const {
    lock,
    unlock
  } = useScrollLock({
    autoLock: Boolean(currentStep)
  });
  useOnChange(Boolean(currentStep), () => {
    if (currentStep) {
      lock();
    } else {
      unlock();
    }
  }, () => {
    unlock();
  });
  const stepProps = {
    currentIndex: onboardingSteps.indexOf(currentStep || "intro"),
    totalSteps: onboardingSteps.length,
    goToStep: stepIndex => setCurrentStep(onboardingSteps[stepIndex])
  };

  /**
   * Disconnect wallet if connected during onboarding
   * This prevents accept terms modal from interfering with onboarding
   */
  const address = useAddress();
  const ready = useIsReady();
  const disconnect = useDisconnect({
    source: "onboarding"
  });
  useEffect(() => {
    if (address && ready && !outroCompleted) {
      void disconnect();
    }
  }, [address, ready, disconnect, outroCompleted]);
  return <FlexColumn className={classNames("dark fixed inset-0 z-onboarding size-full overflow-hidden bg-black", "duration-500 ease-out-quint", !hydrated && "opacity-0", !currentStep && "pointer-events-none opacity-0",
  // Delaying outro
  (currentStep === "outro" || !currentStep) && "delay-300 duration-3500")} role="dialog">
      <OnboardingBackground stepIndex={currentStep ? onboardingSteps.indexOf(currentStep) : -1} />
      <OnboardingHeader className="z-20" currentStep={currentStep} onClose={() => {
      setCompleted();
    }} setStep={setCurrentStep} steps={onboardingSteps} />
      <Flex className="relative z-10 size-full">
        <IntroStep {...stepProps} completed={introCompleted} index={onboardingSteps.indexOf("intro")} onConfirm={() => {
        setIntroCompleted(true);
      }} presented={currentStep === "intro"} />
        <FilmStep {...stepProps} completed={filmCompleted} index={onboardingSteps.indexOf("film")} onConfirm={() => {
        setFilmCompleted(true);
      }} presented={currentStep === "film"} />
        <PersonaStep {...stepProps} completed={Boolean(persona)} index={onboardingSteps.indexOf("persona")} onConfirm={optionId => {
        setPersona(optionId);
      }} presented={currentStep === "persona"} />
        <CurrencyStep {...stepProps} completed={Boolean(currencyPreference)} index={onboardingSteps.indexOf("currency")} onConfirm={optionId => {
        setCurrencyPreference(optionId);
      }} presented={currentStep === "currency"} />
        <RewardsStep {...stepProps} completed={rewardsCompleted} index={onboardingSteps.indexOf("rewards")} onConfirm={() => {
        setRewardsCompleted(true);
      }} presented={currentStep === "rewards"} />
        <OutroStep {...stepProps} completed={outroCompleted} index={onboardingSteps.indexOf("outro")} onConfirm={() => {
        setOutroCompleted(true);
        setCompleted();
        unlock();
      }} presented={currentStep === "outro"} />
      </Flex>
    </FlexColumn>;
});